// @ts-check
// @ts-ignore

import { camelToPascal, snakeToCamel } from '@/common/utils/strings';

/**
 * Note: This file could be incompatible if 
 * Riot.js's version is updated.
 * Make sure to do extensive tests for update.
 * 
 * The assumption is made that no eval() is used in
 * Riot's render phase, or the functions will loss
 * their effectiveness.
 */

/**
 * @typedef {(html: string, bindings: any) => import('@riotjs/dom-bindings').TemplateChunk} TemplateChunkCreator
 * @typedef {(_template: TemplateChunkCreator, expressionTypes, bindingTypes, getComponent) => import('@riotjs/dom-bindings').TemplateChunk} TemplateDefinition
 */

// compiled from './kf-model-template.riot'

/**
 *
 * @param {string} modalName
 * @param {string} modalCloseName
 * @param {string} innerComponentName
 * @param {string} expr1
 * @param {string} expr2
 * @returns {TemplateDefinition}
 */
export const createTemplate = (
  modalName,
  modalCloseName,
  innerComponentName,
  expr1,
  expr2
) => {
  function template(_template, expressionTypes, bindingTypes, getComponent) {
    return _template(`<kf-modal ${expr1}="${expr1}"></kf-modal>`, [
      {
        type: bindingTypes.TAG,
        getComponent: getComponent,
        // eslint-disable-next-line no-unused-vars
        evaluate: function evaluate(_scope) {
          return 'kf-modal';
        },
        slots: [
          {
            id: 'default',
            html: `<${innerComponentName} ${expr2}="${expr2}"></${innerComponentName}>`,
            bindings: [
              {
                type: bindingTypes.TAG,
                getComponent: getComponent,
                // eslint-disable-next-line no-unused-vars
                evaluate: function evaluate(_scope) {
                  return innerComponentName;
                },
                slots: [],
                attributes: [
                  {
                    type: expressionTypes.ATTRIBUTE,
                    name: null,
                    evaluate: function evaluate(_scope) {
                      return _scope.props[modalName].props;
                    },
                  },
                ],
                redundantAttribute: expr2,
                selector: '[' + expr2 + ']',
              },
            ],
          },
        ],
        attributes: [
          {
            type: expressionTypes.ATTRIBUTE,
            name: 'modal',
            evaluate: function evaluate(_scope) {
              return _scope.props[modalName];
            },
          },
          {
            type: expressionTypes.EVENT,
            name: 'on-close',
            evaluate: function evaluate(_scope) {
              return _scope.props[modalCloseName];
            },
          },
        ],
        redundantAttribute: expr1,
        selector: '[' + expr1 + ']',
      },
    ]);
  }
  return template;
};

/**
 *
 * @param  {...TemplateDefinition} templates
 * @returns {TemplateDefinition}
 */
export const concatTemplate = (...templates) => {
  function template(_template, expressionTypes, bindingTypes, getComponent) {
    const fullHtml = [];
    const fullBindings = [];
    for (const template of templates) {
      const { html, bindingsData } = template(
        _template,
        expressionTypes,
        bindingTypes,
        getComponent
      );
      fullHtml.push(html);
      fullBindings.push(...bindingsData);
    }
    const ret = _template.call(this, fullHtml.join(''), fullBindings);
    return ret;
  }
  return template;
};

export const appendTemplate = (root, modals, components) => {
  const templates = [];
  const components2 = {};
  for (let i = 0; i < modals.length; ++i) {
    const { name, close } = modals[i];
    const comp = components[i];
    const compName = comp.name;
    const expr1 = `expr-${compName}-1`;
    const expr2 = `expr-${compName}-2`;
    const newTemplate = createTemplate(name, close, compName, expr1, expr2);
    templates.push(newTemplate);
    components2[camelToPascal(snakeToCamel(compName.replace(/-/g, '_')))] = comp;
  }
  const newRootExports = root.exports
    ? {
      ...root.exports,
      components: {
        ...(root.exports.components ?? {}),
        ...components2,
      },
    }
    : { components: components2 };
  const newRoot = {
    ...root,
    exports: newRootExports,
    template: concatTemplate(root.template, ...templates),
  };
  return newRoot;
};
